import React from "react";
import { Card, CardContent, Typography, Box, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  variant?: "success" | "warning" | "error" | "info" | "default";
  loading?: boolean;
  isAlert?: boolean;
}

export default function KpiCard({
  title,
  value,
  subtitle,
  icon,
  variant = "default",
  loading = false,
  isAlert = false,
}: KpiCardProps) {
  const theme = useTheme();

  // Definir cores baseado no variant
  const getVariantColor = () => {
    switch (variant) {
      case "success":
        return theme.palette.success.main;
      case "warning":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      case "info":
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const variantColor = getVariantColor();

  if (loading) {
    return (
      <Box sx={{ height: "100%", minHeight: 140, p: 2 }}>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={48} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 140,
        p: 3,
        borderLeft: `4px solid ${variantColor}`,
        backgroundColor: isAlert ? "#FFF4F2" : "transparent",
        borderRadius: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: isAlert ? "#FFE8E5" : "#F8F9FA",
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
        {icon && (
          <Box sx={{ color: variantColor, display: "flex" }}>{icon}</Box>
        )}
      </Box>

      <Typography
        variant="h4"
        component="div"
        sx={{ 
          fontSize: "2.25rem",
          fontWeight: 400, 
          color: isAlert ? "#D9534F" : variantColor, 
          mb: 1,
          lineHeight: 1.2
        }}
      >
        {value}
      </Typography>

      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

